/**
 * The PeriodicTableContext component wraps all components and provides a global state object
 * Any component can grab the current state of the app using the useContext hook
 */
import React, { createContext, useEffect, useReducer } from "react";
import axios from "axios";
import _ from "lodash";
import initialState from "../initialState"; // set the initial state object with default values
import { SORT_OPTIONS } from "../utils/constants";
import { API } from "../utils/config";

// Create and export the context for the app which can now be used across the component tree
// e.g. const { availableElements } = useContext(PeriodicTableContext);
export const PeriodicTableContext = createContext();

const { MIDDLEWARE_URL, TOKEN, ELEMENT_ENDPOINT, SEARCH_ENDPOINT } = API;

/**
 * The reducer function takes the current application state, an additional payload (what we want to change in the state), and then returns a new state
 * The reducer will be passed in as an argument within the useReducer hook
 */
const reducer = (state = [], payload) => {
  return {
    ...state,
    ...payload,
  };
};

export const PeriodicTableProvider = ({ children }) => {
  /**
   * The useReducer hook takes in two arguments, a reducer function and intialState ("../initialState.js") object.
   * useReducer returns current state along with a dispatch method to send objects to the reducer function will use to update our global state
   * see @url https://reactjs.org/docs/hooks-reference.html#usereducer
   */
  const [state, dispatch] = useReducer(reducer, initialState);

  const {
    selectedElements,
    filterType,
    formulaSearchTerm,
    productsFetched,
    productsSorted,
    productsLoading,
    productsSortedBy,
    isSearchPerformed,
    availableElements,
    availableWithSelectedElements,
  } = state;

  /**
   * The following udpdate functions are passed in as props to the PeriodicTableContext.Provider so that all components can utilize these functions as needed.
   * For example, the updateFilterType will be used by the Search component to update from "element" to "formula" search type when the select option is changed.
   */
  const updateSelectedElements = (elements) => {
    dispatch({
      selectedElements: elements,
    });
  };

  const updateFilterType = (type) => {
    dispatch({
      filterType: type,
    });
  };

  const updateFormulaSearchTerm = (term) => {
    dispatch({
      formulaSearchTerm: term,
    });
  };

  const updateProductsSortedBy = (sortedBy) => {
    let newSortedProducts;

    switch (sortedBy) {
      case SORT_OPTIONS.RELEVANCE.value:
        newSortedProducts = productsFetched;
        break;
      case SORT_OPTIONS.FEATURED.value:
        newSortedProducts = _.orderBy(
          productsFetched,
          ["is_featured"],
          ["desc"]
        );
        break;
      case SORT_OPTIONS.ALPHA_ASC.value:
        newSortedProducts = _.orderBy(
          productsFetched,
          [(product) => product.name.toLowerCase()],
          ["asc"]
        );
        break;
      case SORT_OPTIONS.ALPHA_DESC.value:
        newSortedProducts = _.orderBy(
          productsFetched,
          [(product) => product.name.toLowerCase()],
          ["desc"]
        );
        break;
      case SORT_OPTIONS.NEWEST.value:
        newSortedProducts = _.orderBy(
          productsFetched,
          (product) => {
            return new Date(product.date_created);
          },
          "desc"
        );
        break;

      default:
        newSortedProducts = productsFetched;
        break;
    }

    dispatch({
      productsSortedBy: sortedBy,
      productsSorted: newSortedProducts,
    });
  };

  /**
   * Get data from the middleware API
   * Sets the following pieces of state when response is successful:
   * 	productsFetched
   * 	productsSorted
   * 	productsSortedBy
   * 	productsLoading
   * 	isSearchPerformed
   * 	availableWithSelectedElements
   */
  async function fetchProducts() {
    dispatch({
      productsLoading: true,
      availableWithSelectedElements: [],
    });

    const params = {
      api_token: TOKEN,
      filter: filterType,
    };

    if (filterType === "elements") {
      params.elements = selectedElements.join(",");
    }

    if (filterType === "formula") {
      params.formula = formulaSearchTerm;
    }

    try {
      const fetchResponse = await axios.post(
        `${MIDDLEWARE_URL}${SEARCH_ENDPOINT}`,
        params
      );

      const products = fetchResponse.data.results.products || [];
      const compatibleElements =
        fetchResponse.data.results.compatibleElements || [];

      dispatch({
        productsFetched: products,
        productsSorted: products,
        productsSortedBy: SORT_OPTIONS.RELEVANCE.value,
        productsLoading: false,
        isSearchPerformed: true,
        availableWithSelectedElements: compatibleElements,
      });
    } catch (error) {
      console.error(error);
      dispatch({
        productsFetched: [],
        productsSorted: [],
        availableWithSelectedElements: [],
        productsLoading: false,
      });
    }
  }

  /**
   * Get all available elements from the middleware API
   * Note: this fetch will occur only once on page load
   */
  async function fetchAllAvailableElements() {
    try {
      const fetchResponse = await axios.get(
        `${MIDDLEWARE_URL}${ELEMENT_ENDPOINT}?api_token=${TOKEN}`
      );
      dispatch({
        availableElements: fetchResponse.data.elements,
      });
    } catch (error) {
      console.error(error);
    }
  }

  /**
   * Kick off our fetching of new products/comptabibleElements
   * Note: the 2nd argument with an array of dependencies will determine when this useEffect runs.
   * In this case, we only want this useEffect hook to run when either selectedElements or formulaSearchTerm updates.
   */
  useEffect(() => {
    if (selectedElements.length || formulaSearchTerm) {
      fetchProducts();
    } else {
      dispatch({
        productsFetched: [],
        productsSorted: [],
        availableWithSelectedElements: [],
        productsLoading: false,
      });
    }
    // eslint-disable-next-line
  }, [selectedElements, formulaSearchTerm]);

  /**
   *  Load all available elements. Note: Including an empty array dependency, this useEffect runs only once
   */
  useEffect(() => {
    fetchAllAvailableElements();
    // eslint-disable-next-line
  }, []);

  const store = {
    selectedElements,
    filterType,
    formulaSearchTerm,
    productsFetched,
    productsSorted,
    productsLoading,
    productsSortedBy,
    isSearchPerformed,
    availableElements,
    availableWithSelectedElements,
    updateSelectedElements,
    updateFilterType,
    updateFormulaSearchTerm,
    updateProductsSortedBy,
    dispatch,
  };

  return (
    /**
     *  passing in our store object as value allows all children components to consume the state/functions contained in the store via the useContext hook
     */
    <PeriodicTableContext.Provider value={store}>
      {children}
    </PeriodicTableContext.Provider>
  );
};
